import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import { when } from 'lit/directives/when.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { consume } from '@lit/context';
import {
  ltnAuthServiceContext,
  nsDirectoryServiceContext,
  nsSettingsServiceContext,
  nsWorkflowServiceContext,
} from '../ns-services/contexts.js';

import { timeOut } from '../ltn-helpers/utils/async.js';
import { Debouncer } from '../ltn-helpers/utils/debouncer.js';

import { NSWorkflowService } from '../ns-workflows/ns-workflow-service.js';
import { NSDirectoryService } from '../ns-services/ns-directory-service.js';
import { NSSettingsService, SettingsObject } from '../ns-settings/ns-settings-service.js';

import WorkflowSchema from '../models/workflow-schema.js';
import type { StartableWorkflow } from '../models/company-workflow.js';
import { listStartableWorkflows } from '../helpers/company-workflow.js';
import { matchesStartable, relationshipLabel } from './connected.js';
import { LtnAuthService } from '../ltn-auth/ltn-auth-service.js';

import { nsFlyoutStyles, nsSharedStyles, nsCardStyles, nsLayoutStyles } from '../ns-styles/index.js';

import { withTimeout } from '../helpers/async.js';
import { showDevTools } from '../helpers/app-mode.js';

import { categorizeWorkflows, categoryColor, WorkflowCategory } from './workflow-categories.js';
import { launchPagePath, isLaunchable, launcherInputs, launchPermission } from './launchable.js';
import { viewPath } from '../helpers/route.js';

import '../ns-icon/ns-icon.js';
import { createLogger } from '../ns-services/logger.js';
import { LiveAnnouncer } from '../helpers/live-announcer.js';
import '../ns-elements/ns-empty-state.js';
import '../ns-elements/ns-loading.js';
import '@awesome.me/webawesome/dist/components/button/button.js';

const logger = createLogger('ns-start-menu');

const LOAD_TIMEOUT_MS = 8000;

@customElement(NSStartMenu.is)
export class NSStartMenu extends LitElement {
  static get is() {
    return 'ns-start-menu';
  }

  @consume({ context: nsWorkflowServiceContext })
  private __workflowService?: NSWorkflowService;

  @consume({ context: nsDirectoryServiceContext })
  private __directory?: NSDirectoryService;

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  @consume({ context: ltnAuthServiceContext })
  private __auth?: LtnAuthService;

  // Connected companies' workflows this company may start; a failed listing just leaves the section out
  @state()
  private __connected: StartableWorkflow[] = [];

  @state()
  private __connectedSearchResults: StartableWorkflow[] = [];

  private __connectedLoad: Promise<void> = Promise.resolve();

  // The relationship picked on each connected workflow that can go through several
  private __pickedRelationship: Record<string, string> = {};

  @property({ type: Boolean, reflect: true })
  open = false;

  @state()
  private __showSearchResults = false;

  @state()
  private __searchLoading = false;

  @state()
  private __searchError = false;

  @state()
  private __searchResults: WorkflowSchema[] = [];

  @state()
  private __launching = false;

  @state()
  private __browseLoading = false;

  @state()
  private __browseError = false;

  @state()
  private __recentSchemas: WorkflowSchema[] = [];

  @state()
  private __suggestedSchemas: WorkflowSchema[] = [];

  @state()
  private __categories: WorkflowCategory[] = [];

  @state()
  private __selectedCategory?: WorkflowCategory;

  private __announcer = new LiveAnnouncer(this);

  private __debounceSearch?: Debouncer;

  @query('input[type="text"]')
  private __input!: HTMLInputElement;

  @query('.browse-heading')
  private __browseHeading?: HTMLElement;

  private __boundHandleInput = () => this.__handleInput();

  static styles = [
    nsLayoutStyles,
    nsSharedStyles,
    nsFlyoutStyles,
    nsCardStyles,
    css`
      :host {
        background: var(--wa-color-surface-raised);
        color: var(--wa-color-text-normal);
        --ns-card-background: var(--wa-color-surface-lowered);
        display: block;
        height: 100%;
      }

      .wrapper {
        padding: var(--wa-space-m);
      }

      .search-bar {
        padding-bottom: var(--wa-space-m);
      }
      .search-bar input {
        width: 100%;
        padding: var(--wa-space-xs) 0;
        border-radius: 0;
        font-size: var(--wa-font-size-xl);
        background-color: transparent;
        color: var(--wa-color-text-normal);
        border-bottom: 1px solid var(--wa-color-text-normal);
      }

      .section {
        /* Lets .results-list actually shrink below its content size so overflow-y can kick in,
           instead of pushing the fixed-height ns-start-menu panel to silently clip it. */
        min-height: 0;
      }
      .section + .section {
        margin-left: var(--wa-space-m);
      }
      .section > .title {
        font-size: var(--wa-font-size-l);
        margin-bottom: var(--wa-space-xs);
        font-weight: 600;
        line-height: normal;
        letter-spacing: normal;
      }

      .category-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5em;
        margin-bottom: var(--wa-space-xs);
      }
      .category-header .title {
        font-size: var(--wa-font-size-l);
        font-weight: 600;
        line-height: normal;
        letter-spacing: normal;
      }

      .results-list {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
      }

      .category-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--wa-space-xs);
        padding: var(--wa-space-xs);
      }

      .category-tile {
        position: relative;
        height: 100px;
        border: 2px solid transparent;
        border-radius: var(--ns-card-border-radius, 0.5em);
        font: inherit;
        cursor: pointer;
      }

      .category-tile:focus-visible {
        border: 2px solid var(--wa-color-focus);
      }

      .category-tile .title {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background-color: var(--ns-theme-container);
        color: var(--wa-color-text-normal);
        padding: var(--wa-space-xs);
        text-align: center;
        border-radius: 0 0 var(--ns-card-border-radius, 0.5em) var(--ns-card-border-radius, 0.5em);
        font-size: var(--wa-font-size-xs);
      }
      .category-tile .category-count {
        position: absolute;
        top: 6px;
        right: 6px;
        min-width: 1.4em;
        padding: 0 0.3em;
        border-radius: var(--wa-border-radius-pill);
        background-color: color-mix(in srgb, var(--ns-theme-white) 85%, transparent);
        color: var(--ns-theme-black);
        font-size: var(--wa-font-size-2xs);
        line-height: 1.4em;
        text-align: center;
      }

      .back-button {
        flex-shrink: 0;
      }

      /* Shared look for every "nothing to show here" case - empty lists, no search results, and
         load/search failures all render through this one treatment instead of each inventing
         their own. */

      .card .header {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 0 0.5em;
      }

      .card .body .description {
        margin: 0.3em 0 0.6em;
        font-size: 0.85em;
        opacity: 0.85;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      wa-spinner {
        --indicator-color: var(--wa-color-text-normal);
      }

      .category-item {
        display: contents;
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__workflowService) throw new Error('Unable to find NSWorkflowService');

    if (!this.__input) throw new Error('Input element not found in shadow DOM');

    // If the user is searching for a workflow, we show the search results
    this.__input.addEventListener('input', this.__boundHandleInput);
  }

  async disconnectedCallback() {
    super.disconnectedCallback();

    if (this.__input) {
      this.__input.removeEventListener('input', this.__boundHandleInput);
    }
  }

  updated(changedProperties: PropertyValues<this>) {
    super.updated(changedProperties);

    if (changedProperties.has('open') && this.open) {
      this.__selectedCategory = undefined;
      this.__loadBrowseData();
    }

    const shouldFocusBrowseHeading =
      changedProperties.has('__selectedCategory' as any) ||
      (changedProperties.has('__browseLoading' as any) && !this.__browseLoading);
    if (shouldFocusBrowseHeading && this.shadowRoot?.activeElement !== this.__input) {
      this.__browseHeading?.focus();
    }
  }

  private async __launchableSchemas(): Promise<WorkflowSchema[]> {
    if (!this.__workflowService) throw new Error('NSWorkflowService not initialized');

    await withTimeout(this.__workflowService.getWorkflowSchema(), LOAD_TIMEOUT_MS);
    const schemas = this.__workflowService.currentWorkflowSchemas ?? [];
    const appSettings = this.__settings?.settings.app as SettingsObject | undefined;
    const devTools = showDevTools(appSettings?.mode as string | undefined);
    return schemas.filter((schema) => {
      const permission = launchPermission(schema);
      return isLaunchable(schema, devTools) && (!permission || (this.__directory?.can(permission) ?? false));
    });
  }

  private async __loadBrowseData() {
    if (!this.__workflowService) throw new Error('NSWorkflowService not initialized');

    this.__browseLoading = true;
    this.__browseError = false;
    this.__announcer.announce('Loading workflows...');

    let schemas: WorkflowSchema[];
    try {
      schemas = await this.__launchableSchemas();
    } catch (err) {
      logger.error('Unable to load workflows for the launcher', err);
      this.__browseLoading = false;
      this.__browseError = true;
      this.__announcer.announce('Something went wrong loading workflows.');
      return;
    }
    this.__browseLoading = false;

    const recentNames = this.__workflowService.getRecentWorkflowNames();
    this.__recentSchemas = recentNames
      .map((name) => schemas.find((schema) => schema.name === name))
      .filter((schema): schema is WorkflowSchema => !!schema);

    const recentNameSet = new Set(this.__recentSchemas.map((schema) => schema.name));
    this.__suggestedSchemas = schemas.filter((schema) => !recentNameSet.has(schema.name)).slice(0, 6);

    this.__categories = categorizeWorkflows(schemas);
    this.__connectedLoad = this.__loadConnected();
  }

  private async __loadConnected() {
    try {
      this.__connected = await withTimeout(listStartableWorkflows(this.__auth?.token), LOAD_TIMEOUT_MS);
    } catch (err) {
      logger.error("Unable to list connected companies' workflows", err);
      this.__connected = [];
    }
  }

  private async __handleInput() {
    const input = this.__input;
    if (!input) throw new Error('Input element not found in shadow DOM');
    if (!this.__workflowService) throw new Error('NSWorkflowService not initialized');

    this.__showSearchResults = input.value.length > 0;

    if (this.__showSearchResults) {
      this.__debounceSearch = Debouncer.debounce(this.__debounceSearch, timeOut.after(200), async () => {
        if (!this.__workflowService) throw new Error('NSWorkflowService not initialized');

        this.__searchLoading = true;
        this.__searchError = false;
        this.__announcer.announce('Searching...');

        let workflows: WorkflowSchema[];
        try {
          workflows = await this.__launchableSchemas();
        } catch (err) {
          logger.error('Unable to search workflows for the launcher', err);
          this.__searchLoading = false;
          this.__searchError = true;
          this.__announcer.announce('Something went wrong searching for workflows.');
          return;
        }
        this.__searchLoading = false;

        await this.__connectedLoad;
        const query = input.value.toLowerCase();
        this.__connectedSearchResults = this.__connected.filter((startable) => matchesStartable(startable, query));
        this.__searchResults = workflows.filter((workflowSchema) => {
          return (
            workflowSchema.name.toLowerCase().includes(query) ||
            workflowSchema.title?.toLowerCase().includes(query) ||
            workflowSchema.description?.toLowerCase().includes(query)
          );
        });
        const found = this.__searchResults.length + this.__connectedSearchResults.length;
        this.__announcer.announce(`${found} result${found === 1 ? '' : 's'} found.`);
      });
    }
  }

  private __retryBrowse() {
    this.__loadBrowseData();
  }

  private __retrySearch() {
    this.__handleInput();
  }

  focusInput() {
    if (!this.__input) return;

    this.__input.focus();
  }

  clear() {
    if (!this.__input) return;

    this.__input.value = '';
    this.__showSearchResults = false;
  }

  private async __searchResultsClick(result: WorkflowSchema) {
    if (this.__launching || !this.__workflowService) return;

    this.__input.value = result.name;
    const launchPage = launchPagePath(result);
    if (launchPage) {
      this.dispatchEvent(new CustomEvent('close-start-menu', { bubbles: true, composed: true }));
      viewPath(launchPage);
      return;
    }
    this.__launching = true;

    // Close the launcher as soon as a selection is made - the launch itself continues in the
    // background and reports success (navigation) or failure (toast) independently.
    this.dispatchEvent(new CustomEvent('close-start-menu', { bubbles: true, composed: true }));

    try {
      const companyId = this.__directory?.company?.id;
      if (!companyId) throw new Error('Unable to find the active company to launch a workflow for');

      const workflowId = await this.__workflowService.launchWorkflow(result, launcherInputs(result, companyId));

      this.dispatchEvent(
        new CustomEvent('launch-workflow', {
          bubbles: true,
          composed: true,
          detail: {
            workflowId,
          },
        }),
      );
    } catch (err) {
      // Error feedback (toast) is already handled inside NSWorkflowService.launchWorkflow.
      logger.error('Unable to launch workflow', err);
    } finally {
      this.__launching = false;
    }
  }

  private async __startConnected(startable: StartableWorkflow) {
    if (this.__launching || !this.__workflowService) return;
    const relationshipId =
      startable.relationships.length > 1
        ? (this.__pickedRelationship[startable.companyWorkflowId] ?? startable.relationships[0]?.id)
        : undefined;
    this.__launching = true;
    this.dispatchEvent(new CustomEvent('close-start-menu', { bubbles: true, composed: true }));
    try {
      const jobId = await this.__workflowService.startConnectedWorkflow(startable, relationshipId);
      viewPath(jobId ? `/feed/job/${jobId}` : '/feed');
    } catch (err) {
      // NSWorkflowService has already toasted it
      logger.error("Unable to start a connected company's workflow", err);
    } finally {
      this.__launching = false;
    }
  }

  private __renderConnectedItem(startable: StartableWorkflow) {
    const pickId = `relationship-${startable.companyWorkflowId}`;
    return html`
      <div class="card result-item" role="listitem">
        <div class="header">
          <div class="title">${startable.title}</div>
        </div>
        <div class="buttons">
          <wa-button
            variant="brand"
            type="button"
            class="card-button"
            ?disabled="${this.__launching}"
            @click="${() => this.__startConnected(startable)}"
          >
            Launch
            <span class="visually-hidden">${startable.title} from ${startable.ownerCompanyName}</span>
          </wa-button>
        </div>
        <div class="body">
          <div class="description">From ${startable.ownerCompanyName}</div>
          ${startable.relationships.length > 1
            ? html`
                <label for=${pickId}>Which relationship</label>
                <select
                  id=${pickId}
                  @change=${(e: Event) =>
                    (this.__pickedRelationship[startable.companyWorkflowId] = (e.target as HTMLSelectElement).value)}
                >
                  ${startable.relationships.map(
                    (relationship, index) => html`
                      <option value=${relationship.id}>${relationshipLabel(relationship, index)}</option>
                    `,
                  )}
                </select>
              `
            : ''}
        </div>
      </div>
    `;
  }

  private __renderWorkflowItem(schema: WorkflowSchema) {
    return html`
      <div class="card result-item" role="listitem">
        <div class="header">
          <div class="title">${schema.title ?? schema.name}</div>
        </div>
        <div class="buttons">
          <wa-button
            variant="brand"
            type="button"
            class="card-button"
            ?disabled="${this.__launching}"
            @click="${() => this.__searchResultsClick(schema)}"
          >
            Launch
            <span class="visually-hidden">${schema.title ?? schema.name}</span>
          </wa-button>
        </div>
        <div class="body">
          ${schema.description
            ? html`
                <div class="description">${schema.description}</div>
              `
            : ''}
        </div>
      </div>
    `;
  }

  // Shared rendering for every empty/failure state - an optional icon, a message, and an optional
  // retry action - so search/browse errors, no-results, and empty lists all look like one system.
  private __renderEmptyState(
    message: string,
    options?: { icon?: string; secondaryMessage?: string; actionLabel?: string; onAction?: () => void },
  ) {
    return html`
      <ns-empty-state
        class="w-100"
        message=${message}
        icon=${ifDefined(options?.icon)}
        secondary=${ifDefined(options?.secondaryMessage)}
        action-label=${ifDefined(options?.onAction ? options.actionLabel : undefined)}
        @action=${() => options?.onAction?.()}
      ></ns-empty-state>
    `;
  }

  render() {
    return html`
      ${this.__announcer.render()}
      <div class="wrapper layout vertical h-100">
        <div class="search-bar">
          <input type="text" placeholder="Type to search for a workflow" aria-label="Search for a workflow" />
        </div>
        <div class="search-results" ?hidden="${!this.__showSearchResults}">
          <!-- Loading State -->
          <ns-loading inline class="h-100" label="Searching..." ?hidden="${!this.__searchLoading}"></ns-loading>
          <!-- Error State -->
          <div class="layout horizontal center-center h-100" ?hidden="${!this.__searchError}">
            ${this.__renderEmptyState('Something went wrong searching for workflows.', {
              actionLabel: 'Try again',
              onAction: () => this.__retrySearch(),
            })}
          </div>
          <!-- Search Results -->
          <div class="layout horizontal center-center h-100" ?hidden="${this.__searchLoading || this.__searchError}">
            ${this.__searchResults.length + this.__connectedSearchResults.length > 0
              ? html`
                  <div class="section flex layout vertical">
                    <h2 class="title">Search Results</h2>
                    <div class="results-list" role="list">
                      ${this.__searchResults.map((result) => this.__renderWorkflowItem(result))}
                      ${this.__connectedSearchResults.map((result) => this.__renderConnectedItem(result))}
                    </div>
                  </div>
                `
              : this.__renderEmptyState('No results found', {
                  icon: 'search',
                  secondaryMessage: 'Try a different search term.',
                })}
          </div>
        </div>
        <div class="layout horizontal h-100" ?hidden="${this.__showSearchResults}">
          <!-- Browse Loading State -->
          <ns-loading inline class="h-100" label="Loading workflows..." ?hidden="${!this.__browseLoading}"></ns-loading>
          <!-- Browse Error State -->
          <div class="layout horizontal center-center h-100" ?hidden="${!this.__browseError}">
            ${this.__renderEmptyState('Something went wrong loading workflows.', {
              actionLabel: 'Try again',
              onAction: () => this.__retryBrowse(),
            })}
          </div>
          <!-- Browse -->
          ${when(!this.__browseLoading && !this.__browseError, () =>
            when(
              this.__selectedCategory,
              () => html`
                <div class="section flex layout vertical">
                  <div class="category-header">
                    <h2 class="title browse-heading" tabindex="-1">${this.__selectedCategory?.name}</h2>
                    <wa-button
                      variant="neutral"
                      type="button"
                      class="card-button back-button"
                      @click="${() => (this.__selectedCategory = undefined)}"
                    >
                      Back
                    </wa-button>
                  </div>
                  <div class="results-list" role="list">
                    ${this.__selectedCategory?.schemas.map((schema) => this.__renderWorkflowItem(schema))}
                  </div>
                </div>
              `,
              () => html`
                <div class="section flex layout vertical">
                  <h2 class="title browse-heading" tabindex="-1">Recent</h2>
                  ${this.__recentSchemas.length > 0
                    ? html`
                        <div class="results-list" role="list">
                          ${this.__recentSchemas.map((schema) => this.__renderWorkflowItem(schema))}
                        </div>
                      `
                    : this.__renderEmptyState('No recently launched workflows yet')}
                </div>
                <div class="section flex layout vertical">
                  <h2 class="title">Suggested</h2>
                  ${this.__suggestedSchemas.length > 0
                    ? html`
                        <div class="results-list" role="list">
                          ${this.__suggestedSchemas.map((schema) => this.__renderWorkflowItem(schema))}
                        </div>
                      `
                    : this.__renderEmptyState('No workflows available')}
                </div>
                ${this.__connected.length > 0
                  ? html`
                      <div class="section flex layout vertical">
                        <h2 class="title">From connected companies</h2>
                        <div class="results-list" role="list">
                          ${this.__connected.map((startable) => this.__renderConnectedItem(startable))}
                        </div>
                      </div>
                    `
                  : ''}
                <div class="section flex layout vertical">
                  <h2 class="title">Categories</h2>
                  <div class="category-grid" role="list">
                    ${this.__categories.map(
                      (category) => html`
                        <div class="category-item" role="listitem">
                          <button
                            type="button"
                            class="category-tile"
                            style="background-color: ${categoryColor(category.name)}"
                            @click="${() => (this.__selectedCategory = category)}"
                          >
                            <span class="category-count">${category.schemas.length}</span>
                            <div class="title">${category.name}</div>
                          </button>
                        </div>
                      `,
                    )}
                  </div>
                </div>
              `,
            ),
          )}
        </div>
      </div>
    `;
  }
}
